import type { MessageHitDto } from '@chat-emp/shared';

import type { InboxItem, ParticipantId } from '../fixtures/domain.js';
import type { InboxRow } from '../inbox/stack.js';
import { cidParts } from '../lib/bridge.js';
import { windowAround } from '../util/snippet.js';

/** One Messages row: the matching message, the loaded chat it belongs to, its snippet. */
export interface HitRow {
  hit: MessageHitDto;
  item: InboxItem;
  snippet: string;
}

/** Same rule as `InboxPanel`'s `hitRows`: a hit whose chat is not loaded is dropped
 *  rather than drawn nameless. Keep the two in step. */
export function joinHits(hits: MessageHitDto[], items: InboxItem[], term: string): HitRow[] {
  const byChannel = new Map(items.map((item) => [cidParts(item.channel.id).channelId, item]));
  const rows: HitRow[] = [];
  for (const hit of hits) {
    const item = byChannel.get(hit.channelId);
    if (item) rows.push({ hit, item, snippet: windowAround(hit.text, term) });
  }
  return rows;
}

/** Same rule as `InboxPanel`'s Jobseeker Details split: the verdict is the person's, and
 *  a visible name match keeps them under Chats. Keep the two in step. */
export function splitProfileOnly(
  rows: InboxRow[],
  profileMatched: ReadonlySet<string>,
  term: string,
): [chats: InboxRow[], candidates: InboxRow[]] {
  const needle = term.trim().toLowerCase();
  if (needle === '' || profileMatched.size === 0) return [rows, []];
  const profileOnly = (who: { id: ParticipantId; name: string }) =>
    profileMatched.has(who.id) && !who.name.toLowerCase().includes(needle);
  const chats: InboxRow[] = [];
  const candidates: InboxRow[] = [];
  for (const row of rows) {
    const who = row.kind === 'stack' ? row.counterpart : row.item.counterpart;
    (profileOnly(who) ? candidates : chats).push(row);
  }
  return [chats, candidates];
}

/** Chats behind the rows, counting each chat inside a folded stack. */
export function countChannels(rows: InboxRow[]): number {
  return rows.reduce((n, row) => n + (row.kind === 'stack' ? row.items.length : 1), 0);
}
